import { Contract, getPartialEarlySettlementEndpoint, PartialEarlySettlement } from 'cpfr-common';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import { ContractHeader, Spinner, useAnalyticsPageViewTracker } from '@cp-shared-19/frontend-ui';
import { ContentSection, Layout, Modal, Notification } from '@vwfs-bronson/bronson-react';
import { LicensePlate } from 'components/license-plate/LicensePlate';
import { dashboardPagePath } from 'components/navigation/paths';
import { CpDataApi } from 'cp-xhr';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FormData, PartialEarlySettlementForm } from './form';
import { getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';

type PartialEarlySettlementUiProps = {
    contract?: Contract;
    customerType: string;
};

type RequestProps = {
    isLoading?: boolean;
    errorCode?: string;
    isSuccess?: boolean;
};

export const PartialEarlySettlementUi: React.FC<PartialEarlySettlementUiProps> = ({ contract, customerType }) => {
    const [earlySettlementRequest, setEarlySettlementRequest] = useState<RequestProps>({});
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);
    // Declared with the other hooks: the component returns early when there is no
    // contract, and a hook must not sit behind that return.
    const { run } = useSubmitOnce();

    useAnalyticsPageViewTracker('earlySettlement', !!contract);

    const { t } = useTranslation(['early-settlement', 'loader']);
    const history = useHistory();
    const handleModalClose = (): void => setEarlySettlementRequest({});

    if (!contract) return null;
    const handleSubmit = (data: FormData): Promise<void> =>
        run(async () => {
            const body: PartialEarlySettlement = {
                ...data,
                amount: Number(data.amount.replace(',', '.')),
                requestDate: formatCpDate(data.requestDate).format('YYYY-MM-DD'),
                contractNumber: contract.contractNumber,
            };

            const reportSubmitted = (): void => {
                setEarlySettlementRequest({ isSuccess: true });
                setShowSuccessNotification(true);
            };

            setEarlySettlementRequest({ isLoading: true });

            // Returned, so the latch is only released once the request answered.
            return CpDataApi.post(getPartialEarlySettlementEndpoint(), body)
                .then(reportSubmitted)
                .catch((error) => {
                    // A duplicate is not a failure: the request is already registered.
                    if (isDuplicateSubmissionError(error)) {
                        reportSubmitted();
                        return;
                    }
                    setEarlySettlementRequest({
                        errorCode: getErrorCode(error),
                    });
                });
        });

    const getErrorMessage = (errorCode: string | undefined) => {
        switch (errorCode) {
            case 'CHANGE_TOO_LATE':
                return 'partial-early-settlement.modal.error.change-to-late';
            case 'EMAIL_NOT_FOUND_OR_PROVIDED':
                return 'partial-early-settlement.modal.error.missing-email';
            default:
                return 'partial-early-settlement.modal.error.sending-problem';
        }
    };

    const { contractNumber, carModel = ' ', licensePlate = '', productTypeFR = '' } = contract;

    return (
        <>
            {earlySettlementRequest.isLoading && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <ContentSection pageWrapSize="medium">
                <Layout>
                    <Layout.Item default="1/1">
                        {showSuccessNotification && (
                            <Notification
                                title={t('success-notification.title')}
                                className="u-mb"
                                visible
                                testId="contract-success-notification"
                                showCloseButton={false}
                                status={'success'}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t('success-notification.text'),
                                    }}
                                />
                            </Notification>
                        )}
                        <ContractHeader
                            carInformation={carModel}
                            contractCategory={`Type de financement: ${productTypeFR}`}
                            contractNumber={`N° dossier: ${contractNumber}`}
                            contractIdentifier={<LicensePlate registrationNumber={licensePlate} />}
                        />
                    </Layout.Item>
                    <Layout.Item default="1/1">
                        <PartialEarlySettlementForm
                            handleSubmit={handleSubmit}
                            errorCode={earlySettlementRequest.errorCode}
                            customerType={customerType}
                        />
                    </Layout.Item>
                </Layout>
            </ContentSection>
            <Modal
                shown={!!earlySettlementRequest.isSuccess}
                status="success"
                onClose={(): void => history.push(dashboardPagePath())}
                onCancel={(): void => history.push(dashboardPagePath())}
                buttonCancelText={t('translation:editable-section-nav.ok')}
                title={t('partial-early-settlement.modal.success.header')}
                testId={'success-modal'}
                aria-live="polite"
                role="status"
                aria-atomic="true"
            >
                {t('partial-early-settlement.modal.success.message')}
            </Modal>
            <Modal
                shown={
                    !!earlySettlementRequest.errorCode &&
                    earlySettlementRequest.errorCode !== 'EMAIL_NOT_FOUND_OR_PROVIDED'
                }
                status="error"
                onClose={handleModalClose}
                onCancel={handleModalClose}
                buttonCancelText={t('translation:editable-section-nav.ok')}
                testId={'error-modal'}
                title={t('partial-early-settlement.modal.error.header')}
            >
                {t(getErrorMessage(earlySettlementRequest.errorCode) || '')}
            </Modal>
        </>
    );
};
